/* EntityGridUserStorage.js */

"use strict";

const UserRemoteStorageBase = require("./UserRemoteStorage");
const _ = require("lodash");

const COLUMN_WIDTH_LABEL = "columnWidth";
const COLUMN_ORDER_LABEL = "columnOrder";
const COLUMN_LOCKED_LABEL = "columnLocked";
const COLUMN_SORTING_LABEL = "columnSorting";
const BASIC_SEARCH_DATA_LABEL = "basicSearchData";
const ADVANCED_SEARCH_DATA_LABEL = "advancedSearchData";
const GROUPING_DATA_LABEL = "groupingData";

const SESSION_LEVEL_KEYS = [BASIC_SEARCH_DATA_LABEL, ADVANCED_SEARCH_DATA_LABEL];

const SHARED_KEYS = [BASIC_SEARCH_DATA_LABEL, ADVANCED_SEARCH_DATA_LABEL, GROUPING_DATA_LABEL];

/**
 * This is an extend subclass of UserRemoteStorageBase,
 * with extra features like _sharedStorageName and gridModel listeners,
 * uses CRUID data operations for entity_grid
 */
class UserDataExtended extends UserRemoteStorageBase {
    constructor(storageRootName, storageKey, userSession, logger, gridModel) {
        super(storageRootName, storageKey, userSession, logger);
        this.setGridModel(gridModel);

        this._storageName = this.getStorageName(storageKey);
        this._sharedStorageName = this.getSharedStorageName(storageKey);
        if (!storageKey) {
            this._logger.info(
                `UserGridData: Missing gridKey parameter: userData will be temporarily stored in "${this._storageName}" section`
            );
        }

        this._resizeHandler = this._resizeHandler.call(this, 300);
    }

    setGridModel(gridModel) {
        if (!gridModel) {
            //this._logger.info("UserGridData: Missing gridModel parameter: Event listeners will be disabled");
            return;
        }

        if (this._gridModel) {
            this._unregisterListeners();
        }

        this._gridModel = gridModel;
        this._registerListeners();
    }

    _registerListeners() {
        if (this._gridModel) {
            this._gridModel.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this)));
            this._gridModel.on(
                "ColumnUserResized",
                (this._onColumnUserResized = this._onColumnUserResized.bind(this))
            );
            this._gridModel.on(
                "LockedColumns",
                (this._onLockedColumns = this._onLockedColumns.bind(this))
            );
            this._gridModel.on(
                "UserReordered",
                (this._onUserReordered = this._onUserReordered.bind(this))
            );
            this._gridModel.on(
                "ColumnOrder",
                (this._onColumnOrderChange = this._onColumnOrderChange.bind(this))
            );
            this._gridModel.on(
                "BasicSearchFilterChangeData",
                (this._onBasicSearchChangeData = this._onBasicSearchChangeData.bind(this))
            );
            this._gridModel.on(
                "AdvancedSearchFilterChangeData",
                (this._onAdvancedSearchChangeData = this._onAdvancedSearchChangeData.bind(this))
            );
            this._gridModel.on(
                "GroupingChanged",
                (this._onGroupingChange = this._onGroupingChange.bind(this))
            );
        }
    }

    _unregisterListeners() {
        if (this._gridModel) {
            this._gridModel.removeListener("UserSorted", this._onUserSorted);
            this._gridModel.removeListener("ColumnUserResized", this._onColumnUserResized);
            this._gridModel.removeListener("LockedColumns", this._onLockedColumns);
            this._gridModel.removeListener("UserReordered", this._onUserReordered);
            this._gridModel.removeListener("ColumnOrder", this._onColumnOrderChange);
            this._gridModel.removeListener(
                "BasicSearchFilterChangeData",
                this._onBasicSearchChangeData
            );
            this._gridModel.removeListener(
                "AdvancedSearchFilterChangeData",
                this._onAdvancedSearchChangeData
            );
            this._gridModel.removeListener("GroupingChanged", this._onGroupingChange);
        }
    }

    shutdown() {
        if (this._storage && _.isFunction(this._storage.removeItem)) {
            this._storage.removeItem(this.getStorageName());
        }
        return super.shutdown().then(() => {
            this._gridModel = undefined;
            this._sharedStorageName = undefined;
            this._resizeHandler = undefined;
            return Promise.resolve();
        });
    }

    getData(key) {
        let storageData = null,
            keyData;
        if (SHARED_KEYS.includes(key)) {
            storageData = this.getStorageData(this._sharedStorageName);
        } else {
            storageData = this.getStorageData(this._storageName);
        }

        if (!storageData) {
            return;
        }

        if (!key) {
            return storageData;
        }

        keyData = storageData[key];

        if (typeof keyData === "string") {
            keyData = JSON.parse(keyData);
        }

        if (
            SESSION_LEVEL_KEYS.includes(key) &&
            this._userSession &&
            keyData &&
            Object.hasOwn(keyData, "sessionToken")
        ) {
            let parsedValue =
                typeof keyData.value === "string" ? JSON.parse(keyData.value) : keyData.value;
            return keyData.sessionToken === this._userSession.token ? parsedValue : "";
        }

        return keyData;
    }

    getSorting() {
        return this.getData(COLUMN_SORTING_LABEL) || {};
    }

    getColOrder() {
        return this.getData(COLUMN_ORDER_LABEL) || [];
    }

    getLocked() {
        return this.getData(COLUMN_LOCKED_LABEL) || [];
    }

    getColWidth() {
        return this.getData(COLUMN_WIDTH_LABEL) || {};
    }

    getBasicSearchData() {
        return this.getData(BASIC_SEARCH_DATA_LABEL) || {};
    }

    getAdvancedSearchData() {
        return this.getData(ADVANCED_SEARCH_DATA_LABEL) || {};
    }

    /**
     * @typedef {Object} GroupingData - Grouping data object
     * @property {string|undefined} groupBy - the group by column name
     * @property {string[]} - list of expanded groups
     */

    /**
     * Function to get stored grouping data
     * @returns {GroupingData}
     */
    getGroupingData() {
        return (
            this.getData(GROUPING_DATA_LABEL) || {
                groupBy: undefined,
                expanded: [],
                prevColStatus: undefined,
            }
        );
    }

    setData(data, key /*, force = false*/) {
        let keyValueData = {},
            storageName = SHARED_KEYS.includes(key) ? this._sharedStorageName : this._storageName;

        if (key && typeof key === "string") {
            storageName = storageName + "." + key;
            if (SESSION_LEVEL_KEYS.includes(key) && this._userSession) {
                keyValueData = {
                    value: JSON.stringify(data),
                    sessionToken: this._userSession.token,
                };
            } else {
                keyValueData = JSON.stringify(data);
            }
        }

        //const userData = force ? data : Object.assign({}, this._storageData, keyValueData);
        this._storageData[key] = data;

        this.updateStorage(keyValueData, storageName);
    }

    updateStorage(keyValueData, storageName) {
        //const storage = this.getStorageData(storageName);
        //let data = Object.assign({}, storage, keyValueData);

        if (typeof keyValueData === "string" || typeof keyValueData === "object") {
            this._storage.setItem(storageName, keyValueData);
        } else {
            this._logger.info("UserGridData: Data is not a valid object");
        }
    }

    updateGrid(keys = []) {
        keys.forEach((key) => {
            switch (key) {
                case COLUMN_WIDTH_LABEL:
                    return this.emit("widthChanged", this._storageData[key]);
                case COLUMN_ORDER_LABEL:
                    return this.emit("orderChanged", this._storageData[key]);
                case COLUMN_LOCKED_LABEL:
                    return this.emit("lockedChanged", this._storageData[key]);
                case COLUMN_SORTING_LABEL:
                    return this.emit("sortingChanged", this._storageData[key]);
                default:
                    return;
            }
        });
    }

    _onColumnUserResized(columnResizeData) {
        this._resizeHandler(columnResizeData);
    }

    _resizeHandler(delay) {
        return _.debounce((columnResizeData) => {
            if (!columnResizeData || !columnResizeData.column) {
                return;
            }
            const columnResized = {};

            columnResized[columnResizeData.column] = columnResizeData.width;
            this.setData(
                Object.assign({}, this.getData(COLUMN_WIDTH_LABEL), columnResized),
                COLUMN_WIDTH_LABEL
            );
        }, delay);
    }

    _onUserSorted(sortData) {
        const sorting = {};
        const sortingCriteria = _.get(
            this._gridModel,
            ["_columns", sortData.sortColumn, "path"],
            sortData.sortColumn
        );
        sorting[sortingCriteria] = sortData.sort === "asc" ? 1 : -1;
        this.setData(sorting, COLUMN_SORTING_LABEL);
    }

    _onLockedColumns(lockedColumns) {
        const updatedColumnsList = this._updateColumnsList(lockedColumns, this.getLocked());
        this.setData(updatedColumnsList, COLUMN_LOCKED_LABEL);
    }

    _onUserReordered(colReorder) {
        const columnOrder = colReorder.columnOrder;
        const updatedColumnsList = this._updateColumnsList(columnOrder, this.getColOrder());
        this.setData(updatedColumnsList, COLUMN_ORDER_LABEL);
    }

    _onColumnOrderChange(columnOrder) {
        const updatedColumnsList = this._updateColumnsList(columnOrder, this.getColOrder());
        this.setData(updatedColumnsList, COLUMN_ORDER_LABEL);
    }

    _onBasicSearchChangeData(filterData) {
        this.setData(filterData, BASIC_SEARCH_DATA_LABEL);
    }

    _onAdvancedSearchChangeData(filterQuery) {
        this.setData(filterQuery, ADVANCED_SEARCH_DATA_LABEL);
    }

    /**
     *
     * @param {Object} groupingData -
     * @param {string} groupingData.groupBy - Group by column name
     * @param {string[]} groupingData.expanded - list of expanded groups
     *
     * @private
     */
    _onGroupingChange(groupingData) {
        this.setData(groupingData, GROUPING_DATA_LABEL);
    }

    /**
     * Function to keep all custom fields stored even if not available in active organization
     * @returns {Array}
     */
    _updateColumnsList(columnOrder, storedColumns) {
        const gridColumns = Object.keys(this._gridModel.getColumns() || {});
        const columnsDifference = _.difference(storedColumns, gridColumns);

        const updatedColumnOrder = columnsDifference.reduce(
            (updatedOrder, colName) => {
                updatedOrder.splice(storedColumns.indexOf(colName), 0, colName);
                return updatedOrder;
            },
            [...columnOrder]
        );

        return _.uniq(updatedColumnOrder.filter(Boolean));
    }

    _onStorageUpdated(eventData) {
        if (eventData.key !== this.storageName) {
            return;
        }
        let newStorage = this._storageData;
        try {
            newStorage = JSON.parse(eventData.newValue) || {};
        } catch (e) {
            this._logger.warn(
                {error: e},
                "UserDataExtended._onStorageUpdated() - Invalid data from the storage"
            );
        }

        const keysUpdated = this._checkDiff(this._storageData, newStorage);
        this.setData(newStorage, null, true);
        this.updateGrid(keysUpdated);
    }

    getStorageName(key = "tmpSection") {
        return `${key}_userData`;
    }

    getSharedStorageName(key) {
        //assuming that gridKey is in format '<solution_name>_<grid_name>',
        //shared storage name will be 'all_<grid_name>'

        const grid_name = String(key).split("_")[1];
        return grid_name ? `all_${grid_name}` : this._storageName;
    }
}

module.exports = UserDataExtended;
